import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Stack,
  Autocomplete,
  TextField,
  CircularProgress,
  Chip,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  DQDashboardRequest,
  DQDashboardResponse,
  SectionTone,
} from "@/app/models/dqDashboard/types";
import dataService from "@/app/services/dataService";
import QuickLookCard from "./quickLookCard";
import MenuCard from "./menuCard";
import DeliverySection from "./deliverySection";
import PageLoading from "@/app/components/shared/pageLoading";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTruck,
  faAddressCard,
  faBasketShopping,
  faRectangleList,
  faFileInvoice,
  faCrown,
} from "@fortawesome/free-solid-svg-icons";
import MostPurchasedProductsCard from "./mostPurchasedProductsCard";
import { useSelector } from "react-redux";
import { RootState } from "@/app/store/store";
import ShipToDialog from "@/app/components/header/shiptoDialog";
import { AddressDropdownItem, OptionGroup } from "@/app/models/checkout/types";
import { Lookup } from "@/app/models/common/types";

export const DQDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<DQDashboardResponse>();
  const [openSection, setOpenSection] = useState<SectionTone | null>(null);
  const [shipToDialogOpen, setShipToDialogOpen] = useState<boolean>(false);

  const defaultShipTo =
    useSelector((state: RootState) => state.userState.user.defaultShipTo) || "";
  const isAuthenticated = useSelector(
    (state: RootState) => state.userState.isAuthenticated
  );
  const customerNumber = useSelector(
    (state: RootState) => state.userState.user?.customerNumber ?? null
  );
  const toggle = (s: SectionTone) =>
    setOpenSection((prev) => (prev === s ? null : s));

  const [customerShipTos, setCustomerShipTos] = useState<OptionGroup[]>([]);
  const [selectedAddresses, setSelectedAddresses] = useState<
    AddressDropdownItem[]
  >([]);

  const [loading, setLoading] = useState(true);
  const [isApiLoading, setIsAPILoading] = useState(false);
  const [shipToloading, setShipToLoading] = useState(true);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dataService.getdQDashboard();
      if (res?.success && res.data) {
        setData(res.data);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const handleOpenShipToDialog = () => {
    setShipToDialogOpen(true);
  };

  const handleCloseShipToDialog = () => {
    setShipToDialogOpen(false);
  };

  useEffect(() => {
    fetchAllCustomerShipTos();
    fetchDashboard();
  }, [fetchDashboard]);

  const fetchAllCustomerShipTos = async () => {
    try {
      setShipToLoading(true);
      const response = await dataService.getAllCustomerShipTos(customerNumber);
      const shipTos: Lookup[] = response.data ?? [];
      if (shipTos.length > 0) {
        const grouped: OptionGroup[] = [
          {
            label: "Addresses",
            options: shipTos.map((addr: Lookup) => ({
              id: addr.id,
              name: addr.displayName,
            })),
          },
        ];

        setCustomerShipTos(grouped);
      }
    } catch (error) {
      console.error("Failed to load templates:", error);
    } finally {
      setShipToLoading(false);
    }
  };

  const shipToSelected = async (addresses: AddressDropdownItem[]) => {
    try {
      setIsAPILoading(true);
      const request: DQDashboardRequest = {
        shipToIds: addresses.map((x) => Number(x.id)),
      };
      const res = await dataService.getFilteredDQDashboard(request);

      if (res?.success && res.data) {
        setData((prev) => {
          if (!prev) return prev;

          return {
            ...prev,
            yesterdaysDeliveries: res.data.yesterdaysDeliveries,
            todaysDeliveries: res.data.todaysDeliveries,
            futureDeliveries: res.data.futureDeliveries,
          };
        });
      }
    } catch (error) {
      console.error("Failed to load templates:", error);
    } finally {
      setIsAPILoading(false);
    }
  };

  const hostname = process.env.REACT_APP_WEB_URL;

  const accountMenuItems = useMemo(() => {
    if (!data)
      return [] as {
        label: string;
        href?: string;
        newTab?: boolean;
        onClick?: () => void;
      }[];

    const items: {
      label: string;
      href?: string;
      newTab?: boolean;
      onClick?: () => void;
    }[] = [];
    if (data.isBillTrustEnabled)
      items.push({
        label: "Online Bill Pay",
        href: `${hostname}/billtrust`,
        newTab: true,
      });
    if (data.isManageUsers)
      items.push({
        label: "Manage Users",
        onClick: () => navigate("/users"),
      });
    if (data.hasOrderNotificationAccess)
      items.push({
        label: "Order Notifications",
        onClick: () => navigate("/ordernotifications"),
      });
    if (data.hasShipTo && customerShipTos?.length > 0)
      items.push({
        label: `Ship To : ${defaultShipTo ?? ""}`,
        onClick: handleOpenShipToDialog,
      });
    items.push({
      label: "Customer Account",
      onClick: () => navigate("/info"),
    });
    items.push({
      label: "User Profile",
      onClick: () => navigate("/profile"),
    });
    items.push({ label: "Log Out", href: `${hostname}/logout` });

    return items;
  }, [data, hostname]);

  const ordersItems = useMemo(() => {
    if (!data) return [] as { label: string; href: string }[];
    const items: { label: string; href: string }[] = [];
    if (data.areOrdersVisible) {
      items.push({
        label: "All Orders",
        href: `${hostname}/order/history?tab=orders`,
      });
      items.push({
        label: "In Progress Orders",
        href: `${hostname}/order/history?tab=inProgress`,
      });
      items.push({
        label: "Completed Orders",
        href: `${hostname}/order/history?tab=completed`,
      });
      items.push({
        label: "Returns",
        href: `${hostname}/Order/CustomerOrdersWithTypes?typeIds=Return Merchandise`,
      });
    }
    return items;
  }, [data]);

  const templatesItems = useMemo(() => {
    if (!data) return [] as { label: string; href: string }[];
    const items: { label: string; href: string }[] = [];
    if (data.areMyMostPurchasedVisible)
      items.push({
        label: "My Most Purchased",
        href: `${hostname}/ProductListItem/FrequentlyPurchasedTemplate`,
      });
    if (data.areTemplatesVisible)
      items.push({
        label: "My Templates",
        href: `${hostname}/ProductList/Public`,
      });
    if (data.areHomeownerEstimatesVisible)
      items.push({
        label: "Homeowner Estimates",
        href: `${hostname}/Quote/List`,
      });
    return items;
  }, [data]);

  const quotesItems = useMemo(() => {
    if (!data) return [] as { label: string; href: string }[];
    const items: { label: string; href: string }[] = [];
    if (data.areOpenQuotesVisible)
      items.push({
        label: "Open Quotes",
        href: `${hostname}/order/history?tab=quotes`,
      });
    if (data.areHoverQuotesVisible)
      items.push({
        label: "HOVER Material Lists",
        href: `${hostname}/order/history?tab=hoverQuotes`,
      });
    return items;
  }, [data]);

  const impactItems = useMemo(() => {
    if (!data)
      return [] as { label: string; href?: string; onClick?: () => void }[];
    const items: { label: string; href?: string; onClick?: () => void }[] = [];
    if (data.areProZoneVisible)
      items.push({
        label: "Lead Generation",
        onClick: () => navigate("/impact-programs/lead-generation"),
      });
    if (data.areImpactRewardsVisible)
      items.push({
        label: "IMPACT Rewards",
        href: `${hostname}/programs/impactrewards`,
      });
    if (data.areSAVRVisible)
      items.push({
        label: "Loyalty Link",
        onClick: () => navigate("/impact-programs/loyalty-link"),
      });
    if (data.areMarketingServicesVisible)
      items.push({
        label: "Marketing Services",
        onClick: () => navigate("/impact-programs/lansing-marketing-services"),
      });
    return items;
  }, [data]);

  const handleChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    const item = accountMenuItems.find((i) => i.label === value);
    if (item?.onClick) item.onClick();
    if (item?.href) window.location.href = item.href;
  };

  if (loading) {
    return <PageLoading />;
  }

  return (
    <>
      <Box
        sx={{ p: { xs: 1.5, md: 3 }, bgcolor: "#f0f7fb" }}
        className="dqDashboard"
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={12}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={2}>
                <Box
                  pl={{ xs: "20px", md: "0", lg: "20px" }}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    flexWrap: "wrap",
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#494440",
                      fontSize: "24px",
                      fontWeight: "bold",
                    }}
                  >
                    My Dashboard
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ display: "inline", fontSize: "16px" }}
                  >
                    Hello, <strong>{data?.username ?? ""}!</strong>
                  </Typography>
                </Box>
              </Grid>
              <Grid
                item
                xs={12}
                md={10}
                display={{ xs: "flex", md: "none" }}
                flexDirection="row"
                alignItems="center"
              >
                <Select
                  value=""
                  onChange={handleChange}
                  displayEmpty
                  size="small"
                  variant="outlined"
                  IconComponent={() => (
                    <span style={{ fontSize: "10px" }}>▼</span>
                  )} // custom arrow
                  sx={{
                    bgcolor: "transparent",
                    border: "none",
                    boxShadow: "none",
                    "& fieldset": { border: "none" },
                    "& .MuiSelect-select": {
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      fontWeight: 700,
                      color: "#494440",
                      fontSize: "15px",
                      "&:focus": { backgroundColor: "transparent" },
                    },
                    "& .MuiPaper-root": {
                      mt: 1,
                      borderRadius: 2,
                      boxShadow: "0px 4px 12px rgba(0,0,0,0.15)",
                      minWidth: 190,
                    },
                  }}
                  renderValue={() => (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box className="iconHeader">
                        <FontAwesomeIcon
                          icon={faAddressCard}
                          color="#666666"
                          size="sm"
                        />
                      </Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "15px" }}>
                        My Account
                      </Typography>
                    </Box>
                  )}
                >
                  {accountMenuItems.map((item) => (
                    <MenuItem
                      key={item.label}
                      value={item.label}
                      sx={{
                        fontSize: "14px",
                        color: "#54afe2",
                        px: 2,
                        py: 1,
                        "&.Mui-selected": { backgroundColor: "transparent" },
                        "&.Mui-selected:hover": { backgroundColor: "#f2f8fd" },
                      }}
                    >
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={12} md={10}>
                <QuickLookCard data={data ?? undefined} />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} md={12}>
            <Grid container spacing={2} alignItems="stretch">
              <Grid
                item
                xs={12}
                md={2}
                sx={{ display: { xs: "none", md: "block" } }}
              >
                <MenuCard
                  title="My Account"
                  icon={
                    <Box className="iconHeader">
                      <FontAwesomeIcon
                        icon={faAddressCard}
                        color="#666666"
                        size="sm"
                      />
                    </Box>
                  }
                  items={accountMenuItems}
                />
              </Grid>
              <Grid item xs={12} md={10}>
                <Card
                  variant="outlined"
                  sx={{
                    bgcolor: { xs: "transparent", md: "#fff" },
                    border: { xs: "none", md: "2px solid #e6ecf0" },
                    borderRadius: "10px",
                  }}
                >
                  <CardHeader
                    sx={{
                      "& .MuiCardHeader-title": {
                        fontWeight: 600,
                        fontSize: 20,
                      },
                      paddingBottom: "0px",
                      display: { xs: "none", md: "flex" },
                    }}
                    title={
                      <Stack direction="row" alignItems="center">
                        <Box className="iconHeader">
                          <FontAwesomeIcon
                            icon={faTruck}
                            color="#666666"
                            size="sm"
                          />
                        </Box>
                        <Typography
                          sx={{
                            fontFamily: '"Montserrat", "Verdana", sans-serif',
                            fontSize: 14,
                            fontWeight: "bold",
                            color: "#494440",
                          }}
                        >
                          Delivery Schedule
                        </Typography>
                      </Stack>
                    }
                    action={
                      <Box
                        sx={{
                          display:
                            data?.hasShipTo && customerShipTos?.length > 0
                              ? "flex"
                              : "none",
                          alignItems: "center",
                          gap: 1,
                          width: "100%",
                          maxWidth: { md: 400, lg: 800 },
                          minWidth: { md: 400 },
                          flexWrap: "nowrap",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ minWidth: 80, textAlign: "right" }}
                        >
                          Ship To:
                        </Typography>
                        <Autocomplete
                          multiple
                          fullWidth
                          size="small"
                          options={customerShipTos.flatMap(
                            (group) => group.options
                          )}
                          getOptionLabel={(option) => option.name}
                          isOptionEqualToValue={(option, value) =>
                            option.id === value?.id
                          }
                          value={selectedAddresses}
                          onChange={(_, newValue) => {
                            setSelectedAddresses(
                              newValue as AddressDropdownItem[]
                            );
                            shipToSelected(newValue as AddressDropdownItem[]);
                          }}
                          filterSelectedOptions
                          slotProps={{ popper: { sx: { zIndex: 2100 } } }}
                          renderTags={(value, getTagProps) =>
                            value.map((o, i) => (
                              <Chip
                                label={typeof o === "string" ? o : o.name}
                                variant="outlined"
                                {...getTagProps({ index: i })}
                                key={typeof o === "string" ? o : o.id}
                              />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder={
                                selectedAddresses.length === 0
                                  ? "Select Ship To"
                                  : ""
                              }
                              size="small"
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {shipToloading && (
                                      <CircularProgress size={20} />
                                    )}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      </Box>
                    }
                  />
                  <Box
                    sx={{
                      display: {
                        xs:
                          data?.hasShipTo && customerShipTos?.length > 0
                            ? "flex"
                            : "none",
                        md: "none",
                      },
                      flexDirection: "column",
                      alignItems: "left",
                      mb: 2,
                      width: "100%",
                    }}
                  >
                    <Typography variant="body2" sx={{ minWidth: 80 }}>
                      Ship To:
                    </Typography>
                    <Autocomplete
                      multiple
                      fullWidth
                      size="small"
                      options={customerShipTos.flatMap(
                        (group) => group.options
                      )}
                      getOptionLabel={(option) => option.name}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value?.id
                      }
                      value={selectedAddresses}
                      onChange={(_, newValue) => {
                        setSelectedAddresses(newValue as AddressDropdownItem[]);
                        shipToSelected(newValue as AddressDropdownItem[]);
                      }}
                      filterSelectedOptions
                      slotProps={{ popper: { sx: { zIndex: 2100 } } }}
                      renderTags={(value, getTagProps) =>
                        value.map((o, i) => (
                          <Chip
                            label={typeof o === "string" ? o : o.name}
                            variant="outlined"
                            {...getTagProps({ index: i })}
                            key={typeof o === "string" ? o : o.id}
                            sx={{ fontSize: 11 }}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={
                            selectedAddresses.length === 0
                              ? "Select Ship To"
                              : ""
                          }
                          size="small"
                          sx={{
                            fontSize: 11,
                            bgcolor: "#fff",
                            borderRadius: 1,
                            "& .MuiOutlinedInput-root": {
                              bgcolor: "#fff",
                            },
                          }}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {shipToloading && (
                                  <CircularProgress size={20} />
                                )}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      ListboxProps={{
                        sx: {
                          fontSize: 13,
                        },
                      }}
                    />
                  </Box>

                  <CardContent sx={{ padding: { xs: "0", md: "16px" } }}>
                    <DeliverySection
                      title="Yesterday's Deliveries"
                      tone="yesterday"
                      orders={data?.yesterdaysDeliveries ?? []}
                      expanded={openSection === "yesterday"}
                      onToggle={() => toggle("yesterday")}
                    />
                    <DeliverySection
                      title="Today's Deliveries"
                      tone="today"
                      orders={data?.todaysDeliveries ?? []}
                      expanded={openSection === "today"}
                      onToggle={() => toggle("today")}
                    />
                    <DeliverySection
                      title="Future Deliveries"
                      tone="future"
                      orders={data?.futureDeliveries ?? []}
                      expanded={openSection === "future"}
                      onToggle={() => toggle("future")}
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
          {isApiLoading ? (
            <Box
              sx={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                bgcolor: "transparent",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1800,
                pointerEvents: "auto",
              }}
            >
              <CircularProgress size={40} sx={{ color: "#1976d2" }} />
            </Box>
          ) : (
            <>
              <Grid item xs={12} sm={12} container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <MenuCard
                    title="My Orders"
                    icon={
                      <Box className="iconHeader">
                        <FontAwesomeIcon
                          icon={faBasketShopping}
                          color="#666666"
                          size="sm"
                        />
                      </Box>
                    }
                    items={ordersItems}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <MenuCard
                    title="Templates"
                    icon={
                      <Box className="iconHeader">
                        <FontAwesomeIcon
                          icon={faRectangleList}
                          color="#666666"
                          size="sm"
                        />
                      </Box>
                    }
                    items={templatesItems}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <MenuCard
                    title="Quotes"
                    icon={
                      <Box className="iconHeader">
                        <FontAwesomeIcon
                          icon={faFileInvoice}
                          color="#666666"
                          size="sm"
                        />
                      </Box>
                    }
                    items={quotesItems}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <MenuCard
                    title="IMPACT Programs"
                    icon={
                      <Box className="iconHeader">
                        <FontAwesomeIcon
                          icon={faCrown}
                          color="#666666"
                          size="sm"
                        />
                      </Box>
                    }
                    items={impactItems}
                  />
                </Grid>
              </Grid>
            </>
          )}
          {data?.products && data?.products.length > 0 && (
            <Grid item xs={12} md={12}>
              <MostPurchasedProductsCard data={data.products} />
            </Grid>
          )}
        </Grid>
      </Box>
      <>
        {isAuthenticated && (
          <ShipToDialog
            open={shipToDialogOpen}
            onClose={handleCloseShipToDialog}
          />
        )}
      </>
    </>
  );
};

export default DQDashboard;
